// Meal plan state management hook
// Manages weekly meal plans with backend sync
// Follows same pattern as useInventory for consistency

import { useState, useEffect, useCallback, useRef } from 'react';
import { getMealPlan, saveMealPlan, getWeekStart } from '../utils/mealPlanApi';

export default function useMealPlan(getToken, isAuthenticated) {
  // ============ STATE ============

  const [meals, setMeals] = useState([]);
  const [weekStart, setWeekStart] = useState(() => getWeekStart());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Track if initial load is done
  const initialLoadDone = useRef(false);
  
  // Track current week to detect changes
  const currentWeekRef = useRef(weekStart);

  // ============ LOAD FROM BACKEND ============

  const loadMealPlan = useCallback(async (week = weekStart) => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const token = await getToken();
      const result = await getMealPlan(week, token);
      
      if (result.success) {
        setMeals(result.plan?.meals || []);
      }
    } catch (err) {
      console.error('Failed to load meal plan:', err);
    } finally {
      setIsLoading(false);
      initialLoadDone.current = true;
    }
  }, [isAuthenticated, getToken, weekStart]);

  // Load on mount and when week changes
  useEffect(() => {
    if (currentWeekRef.current !== weekStart || !initialLoadDone.current) {
      currentWeekRef.current = weekStart;
      loadMealPlan(weekStart);
    }
  }, [weekStart, loadMealPlan]);

  // ============ SAVE TO BACKEND ============

  const saveToBackend = useCallback(async (updatedMeals) => {
    if (!isAuthenticated) return;

    try {
      setIsSaving(true);
      const token = await getToken();
      await saveMealPlan(weekStart, updatedMeals, token);
    } catch (err) {
      console.error('Failed to save meal plan:', err);
    } finally {
      setIsSaving(false);
    }
  }, [isAuthenticated, getToken, weekStart]);

  // ============ HANDLERS ============

  // Add or update a meal for a specific date
  const addMeal = useCallback((meal) => {
    if (!meal?.date) return;

    setMeals(prev => {
      // Remove existing meal for this date
      const filtered = prev.filter(m => m.date !== meal.date);
      const updated = [...filtered, meal];
      
      // Save to backend
      saveToBackend(updated);
      
      return updated;
    });
  }, [saveToBackend]);

  // Remove meal from a specific date
  const removeMeal = useCallback((date) => {
    setMeals(prev => {
      const updated = prev.filter(m => m.date !== date);
      saveToBackend(updated);
      return updated;
    });
  }, [saveToBackend]);

  // Skip a day (mark as skipped instead of removing)
  const skipDay = useCallback((date) => {
    const skipEntry = {
      date,
      type: 'dinner',
      status: 'skipped',
      recipeId: null,
      recipeName: null,
      notes: ''
    };

    setMeals(prev => {
      const filtered = prev.filter(m => m.date !== date);
      const updated = [...filtered, skipEntry];
      saveToBackend(updated);
      return updated;
    });
  }, [saveToBackend]);

  // Clear a day completely
  const clearDay = useCallback((date) => {
    removeMeal(date);
  }, [removeMeal]);

  // Navigate to different week
  const changeWeek = useCallback((newWeekStart) => {
    setWeekStart(newWeekStart);
  }, []);

  // Get meal for a specific date
  const getMealForDate = useCallback((date) => {
    return meals.find(m => m.date === date) || null;
  }, [meals]);

  // ============ COMPUTED VALUES ============

  const plannedMeals = meals.filter(m => m.status === 'planned');
  const skippedDays = meals.filter(m => m.status === 'skipped');

  // ============ RETURN ============

  return {
    // State
    meals,
    weekStart,
    isLoading,
    isSaving,

    // Computed
    plannedMeals,
    skippedDays,

    // Handlers
    addMeal,
    removeMeal,
    skipDay,
    clearDay,
    changeWeek,
    getMealForDate,
    loadMealPlan,
    setWeekStart: changeWeek
  };
}
